import { formatUrl, route } from './route';

const forProduct = (productId: number) => ({ id: String(productId) });

const forClient = (clientId: number) => ({ id: String(clientId) });

const forExport = (productId: number, exportId: number) => ({
    id: String(productId),
    exportid: String(exportId)
});

const forImport = (productId: number, importId: number) => ({
    id: String(productId),
    importId: String(importId)
});

const forScript = (productId: number, scriptId: number) => ({
    id: String(productId),
    scriptid: String(scriptId)
});

const forProject = (productId: number, projectId: number) => ({
    id: String(productId),
    projectId: String(projectId)
});

const forSubscription = (productId: number, subscriptionId: number) => ({
    id: String(productId),
    subscriptionId: String(subscriptionId)
});

export const productRoutes = {
    list: (clientId: number) => route('/(app)/clients/[id=integer]/products', forClient(clientId)),
    create: (clientId: number) => route('/(app)/clients/[id=integer]/products/new', forClient(clientId)),
    detail: (productId: number) => route('/(app)/products/[id=integer]', forProduct(productId)),
    update: (productId: number) => route('/(app)/products/[id=integer]/update', forProduct(productId)),
    configure: (productId: number) => route('/(app)/products/[id=integer]/configure', forProduct(productId)),
    fields: (productId: number) => route('/(app)/products/[id=integer]/fields', forProduct(productId)),
    obligationFields: (productId: number) =>
        route('/(app)/products/[id=integer]/obligationfields', forProduct(productId)),
    assetFields: (productId: number) => route('/(app)/products/[id=integer]/assetfields', forProduct(productId)),
    projectFields: (productId: number) => route('/(app)/products/[id=integer]/projectfields', forProduct(productId)),
    segments: (productId: number) => route('/(app)/products/[id=integer]/segments', forProduct(productId)),
    auditHistory: (productId: number) => route('/(app)/products/[id=integer]/audit-history', forProduct(productId)),
    usingBlueprint: (productId: number) => route('/(app)/products/[id=integer]/using-blueprint', forProduct(productId)),
    paymentSpread: (productId: number) => route('/(app)/products/[id=integer]/payment-spread', forProduct(productId)),
    creditReporting: (productId: number) =>
        route('/(app)/products/[id=integer]/credit-reporting', forProduct(productId)),

    obligations: (productId: number, params?: URLSearchParams) =>
        formatUrl(route('/(app)/products/[id=integer]/obligations/[[segmentKey]]', forProduct(productId)), params),
    obligationsSegment: (productId: number, segmentKey: string, params?: URLSearchParams) =>
        formatUrl(
            route('/(app)/products/[id=integer]/obligations/[[segmentKey]]', {
                ...forProduct(productId),
                segmentKey
            }),
            params
        ),
    obligors: (productId: number, params?: URLSearchParams) =>
        formatUrl(route('/(app)/products/[id=integer]/obligors/[[segmentKey]]', forProduct(productId)), params),
    obligorsSegment: (productId: number, segmentKey: string, params?: URLSearchParams) =>
        formatUrl(
            route('/(app)/products/[id=integer]/obligors/[[segmentKey]]', {
                ...forProduct(productId),
                segmentKey
            }),
            params
        ),

    dataExports: {
        list: (productId: number) => route('/(app)/products/[id=integer]/dataexports', forProduct(productId)),
        create: (productId: number) =>
            route('/(app)/products/[id=integer]/dataexports/[[exportid=integer]]/edit', forProduct(productId)),
        edit: (productId: number, exportId: number) =>
            route('/(app)/products/[id=integer]/dataexports/[[exportid=integer]]/edit', forExport(productId, exportId)),
        view: (productId: number, exportId: number) =>
            route('/(app)/products/[id=integer]/dataexports/[exportid=integer]/view', forExport(productId, exportId)),
        history: (productId: number, exportId: number) =>
            route(
                '/(app)/products/[id=integer]/dataexports/[exportid=integer]/history',
                forExport(productId, exportId)
            ),
        content: (productId: number, exportId: number, historyId: number) =>
            route('/(app)/products/[id=integer]/dataexports/[exportid=integer]/history/[historyId=integer]/content', {
                ...forExport(productId, exportId),
                historyId: String(historyId)
            })
    },

    dataImports: {
        list: (productId: number) => route('/(app)/products/[id=integer]/dataimports', forProduct(productId)),
        create: (productId: number) =>
            route('/(app)/products/[id=integer]/dataimports/[[importId=integer]]/edit', forProduct(productId)),
        edit: (productId: number, importId: number) =>
            route('/(app)/products/[id=integer]/dataimports/[[importId=integer]]/edit', forImport(productId, importId)),
        view: (productId: number, importId: number) =>
            route('/(app)/products/[id=integer]/dataimports/[importId=integer]/view', forImport(productId, importId)),
        history: (productId: number, importId: number) =>
            route(
                '/(app)/products/[id=integer]/dataimports/[importId=integer]/history',
                forImport(productId, importId)
            ),
        historyRecords: (productId: number, importId: number, historyId: number) =>
            route('/(app)/products/[id=integer]/dataimports/[importId=integer]/history/[historyId=integer]/records', {
                ...forImport(productId, importId),
                historyId: String(historyId)
            }),
        spreadExceptions: (productId: number) =>
            route('/(app)/products/[id=integer]/dataimports/spread-exceptions', forProduct(productId))
    },

    documents: {
        list: (productId: number) => route('/(app)/products/[id=integer]/documents', forProduct(productId)),
        create: (productId: number) =>
            route('/(app)/products/[id=integer]/documents/edit/[[documenttemplateid=integer]]', forProduct(productId)),
        edit: (productId: number, documentTemplateId: number) =>
            route('/(app)/products/[id=integer]/documents/edit/[[documenttemplateid=integer]]', {
                ...forProduct(productId),
                documenttemplateid: String(documentTemplateId)
            }),

        pages: {
            list: (productId: number) => route('/(app)/products/[id=integer]/documents/pages', forProduct(productId)),
            create: (productId: number) =>
                route(
                    '/(app)/products/[id=integer]/documents/pages/edit/[[pagetemplateid=integer]]',
                    forProduct(productId)
                ),
            edit: (productId: number, pageTemplateId: number) =>
                route('/(app)/products/[id=integer]/documents/pages/edit/[[pagetemplateid=integer]]', {
                    ...forProduct(productId),
                    pagetemplateid: String(pageTemplateId)
                })
        }
    },

    fees: {
        list: (productId: number) => route('/(app)/products/[id=integer]/fees', forProduct(productId)),
        create: (productId: number) => route('/(app)/products/[id=integer]/fees/new', forProduct(productId)),
        edit: (productId: number, feeId: number) =>
            route('/(app)/products/[id=integer]/fees/[feeId=integer]/edit', {
                ...forProduct(productId),
                feeId: String(feeId)
            })
    },

    ledgers: {
        list: (productId: number) => route('/(app)/products/[id=integer]/ledgers', forProduct(productId)),
        create: (productId: number) =>
            route('/(app)/products/[id=integer]/ledgers/edit/[[ledgerCode]]', forProduct(productId)),
        edit: (productId: number, ledgerCode: string) =>
            route('/(app)/products/[id=integer]/ledgers/edit/[[ledgerCode]]', {
                ...forProduct(productId),
                ledgerCode
            })
    },

    scripts: {
        list: (productId: number) => route('/(app)/products/[id=integer]/scripts', forProduct(productId)),
        detail: (productId: number, scriptId: number) =>
            route('/(app)/products/[id=integer]/scripts/[scriptid=integer]', forScript(productId, scriptId)),
        create: (productId: number) =>
            route('/(app)/products/[id=integer]/scripts/[[scriptid=integer]]/edit', forProduct(productId)),
        edit: (productId: number, scriptId: number) =>
            route('/(app)/products/[id=integer]/scripts/[[scriptid=integer]]/edit', forScript(productId, scriptId)),
        version: (productId: number, scriptId: number, releaseId: number) =>
            formatUrl(
                route('/(app)/products/[id=integer]/scripts/[[scriptid=integer]]/edit', forScript(productId, scriptId)),
                new URLSearchParams({ release: String(releaseId) })
            ),
        releases: (productId: number) => route('/(app)/products/[id=integer]/scripts/releases', forProduct(productId)),
        release: (productId: number, releaseId: number, compareTo?: number | null) =>
            formatUrl(
                route('/(app)/products/[id=integer]/scripts/releases/[releaseId=integer]', {
                    id: String(productId),
                    releaseId: String(releaseId)
                }),
                compareTo ? new URLSearchParams({ compareTo: String(compareTo) }) : undefined
            ),
        spreadSimulator: (productId: number) =>
            route('/(app)/products/[id=integer]/scripts/payment/test', forProduct(productId)),
        workflowInstances: (productId: number, scriptId: number) =>
            route(
                '/(app)/products/[id=integer]/scripts/[scriptid=integer]/workflow-instances',
                forScript(productId, scriptId)
            ),
        workflowInstance: (productId: number, scriptId: number, instanceId: number) =>
            route('/(app)/products/[id=integer]/scripts/[scriptid=integer]/workflow-instances/[instanceId=integer]', {
                ...forScript(productId, scriptId),
                instanceId: String(instanceId)
            })
    },

    projects: {
        list: (productId: number) => route('/(app)/products/[id=integer]/projects', forProduct(productId)),
        create: (productId: number) => route('/(app)/products/[id=integer]/projects/new', forProduct(productId)),
        edit: (productId: number, projectId: number) =>
            route('/(app)/products/[id=integer]/projects/[projectId=integer]/edit', forProject(productId, projectId)),
        instanceFields: (productId: number, projectId: number) =>
            route('/(app)/products/[id=integer]/projects/[projectId=integer]/fields', forProject(productId, projectId))
    },

    returnReasons: {
        list: (productId: number) => route('/(app)/products/[id=integer]/returnreasons', forProduct(productId)),
        create: (productId: number) => route('/(app)/products/[id=integer]/returnreasons/new', forProduct(productId)),
        edit: (productId: number, returnReasonId: number) =>
            route('/(app)/products/[id=integer]/returnreasons/[returnReasonId=integer]/edit', {
                ...forProduct(productId),
                returnReasonId: String(returnReasonId)
            })
    },

    stateFeeTables: {
        list: (productId: number) => route('/(app)/products/[id=integer]/statefeetables', forProduct(productId)),
        create: (productId: number) => route('/(app)/products/[id=integer]/statefeetables/new', forProduct(productId)),
        edit: (productId: number, stateFeeTableId: number) =>
            route('/(app)/products/[id=integer]/statefeetables/edit/[statefeetableid=integer]', {
                ...forProduct(productId),
                statefeetableid: String(stateFeeTableId)
            })
    },

    webhooks: {
        list: (productId: number) => route('/(app)/products/[id=integer]/webhooks', forProduct(productId)),
        create: (productId: number) => route('/(app)/products/[id=integer]/webhooks/new', forProduct(productId)),
        edit: (productId: number, subscriptionId: number) =>
            route(
                '/(app)/products/[id=integer]/webhooks/[subscriptionId=integer]/edit',
                forSubscription(productId, subscriptionId)
            ),
        deliveries: (productId: number, subscriptionId: number) =>
            route(
                '/(app)/products/[id=integer]/webhooks/[subscriptionId=integer]/deliveries',
                forSubscription(productId, subscriptionId)
            )
    }
};
